import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActivityFeed, ActivityItem, ActivitySegment } from 'app/data/types/activity';
import { ActivityFilter, resolveActivityFilter } from 'app/shared/consts/activity';
import { useRememberFeedView } from 'app/shared/hooks/use-feed-view-memory';

/*
 * Nothing here re-orders the feed.
 *
 * search-service sorts both panes — Upcoming by `startAt` ascending, Completed by `endAt` descending —
 * and the feed is cursor-paginated on exactly that sort. Re-sorting a page locally is therefore not
 * merely redundant: an item from page two can sort above items already on page one, so the list
 * reshuffles as the user scrolls, and further scrolling makes it worse.
 *
 * The sort that lived here was written for the client-side fan-out (VIS-1471), when the FE merged
 * several sources and genuinely had to order them. VIS-1541 removed the fan-out; this survived it
 * because it lives in the filter hook and referenced none of the deleted source queries.
 *
 * It also carried a rule that a work order has "no scheduled date" and must sink to the bottom, which
 * is what put a request raised two hours ago below an event in 2027. A work order is placed by its
 * last update (VIS-1888), which the server now sorts it on.
 */

const byFilter = (items: ActivityItem[], filter: ActivityFilter): ActivityItem[] => (
  filter === 'all' ? items : items.filter((i) => i.type === filter)
);

interface UseActivityFilter {
  segment: ActivitySegment;
  setSegment: (segment: ActivitySegment) => void;
  filter: ActivityFilter;
  setFilter: (filter: ActivityFilter) => void;
  /** Ordered, filtered items for the current segment. */
  items: ActivityItem[];
  /** The active filter has no items in EITHER pane — show the empty state. */
  isEmptyForFilter: boolean;
}

const SEGMENTS: ActivitySegment[] = ['upcoming', 'completed'];

export const useActivityFilter = (feed: ActivityFeed): UseActivityFilter => {
  // segment + filter live in the URL query so browser history (native back + the desktop back button
  // from the booking detail) restores the active tabs on the way back. `replace` keeps tab switches out
  // of the history stack.
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL covers going back inside the webview; storage covers the host relaunching the miniapp at
  // its configured launch URL, where there is no query string left to read.
  const rememberFeedView = useRememberFeedView();
  const tabParam = searchParams.get('tab') as ActivitySegment | null;
  const segment: ActivitySegment = tabParam && SEGMENTS.includes(tabParam) ? tabParam : 'upcoming';
  const filter: ActivityFilter = resolveActivityFilter(searchParams.get('filter'));

  const setSegment = useCallback(
    (next: ActivitySegment) => {
      const params = new URLSearchParams(searchParams);
      params.set('tab', next);
      setSearchParams(params, { replace: true });
      rememberFeedView(next, filter);
    },
    [searchParams, setSearchParams, rememberFeedView, filter],
  );

  const setFilter = useCallback(
    (next: ActivityFilter) => {
      const params = new URLSearchParams(searchParams);
      params.set('filter', next);
      setSearchParams(params, { replace: true });
      rememberFeedView(segment, next);
    },
    [searchParams, setSearchParams, rememberFeedView, segment],
  );

  const items = useMemo(() => {
    const base = segment === 'upcoming' ? feed.upcoming : feed.completed;
    return byFilter(base, filter);
  }, [feed, segment, filter]);

  const isEmptyForFilter = useMemo(
    () => byFilter(feed.upcoming, filter).length === 0 && byFilter(feed.completed, filter).length === 0,
    [feed, filter],
  );

  return { segment, setSegment, filter, setFilter, items, isEmptyForFilter };
};
